import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { filterProductosMock, getMockProductosCatalogo } from '../../data/mockProductosCatalogo';
import { productService } from '../../services/productService';

/**
 * Buscador de productos.
 * - mode "mock": catálogo local (demo).
 * - mode "insumo": API /products?insumo_only=1&active_only=1 (solo insumos concesionaria/restaurantes),
 *   o filtrado en cliente si se pasa `insumoCatalog` (misma carga que el formulario de receta).
 */
const ProductoBuscador = ({
  mode = 'mock',
  value,
  onChange,
  /** Solo mode insumo: { productId, label, sku, unit_of_measure } */
  onSelectProduct,
  productId = null,
  /** Si se define (p. ej. desde GET /recetas/form-data), no se llama a la API: se filtra en cliente */
  insumoCatalog = undefined,
  placeholder = 'Buscar producto…',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value || '');
  const [highlight, setHighlight] = useState(0);
  const [panelStyle, setPanelStyle] = useState(null);
  const [apiResults, setApiResults] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const listboxIdRef = useRef(`producto-buscador-list-${Math.random().toString(36).slice(2, 11)}`);

  useEffect(() => {
    setText(value || '');
  }, [value]);

  const filteredMock = useMemo(() => filterProductosMock(text), [text]);

  const useInsumoCatalog = mode === 'insumo' && insumoCatalog != null;

  const insumoCatalogRows = useMemo(() => {
    if (!useInsumoCatalog || !Array.isArray(insumoCatalog)) return [];
    return insumoCatalog.map((p) => ({
      id: p.id,
      name: p.name,
      code: p.sku || p.code || '',
      unit_of_measure: p.unit_of_measure,
    }));
  }, [useInsumoCatalog, insumoCatalog]);

  const filteredInsumoLocal = useMemo(() => {
    if (!useInsumoCatalog) return null;
    const q = text.trim().toLowerCase();
    if (!q) return insumoCatalogRows;
    return insumoCatalogRows.filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.code && String(p.code).toLowerCase().includes(q))
    );
  }, [useInsumoCatalog, insumoCatalogRows, text]);

  const loadInsumos = useCallback(async (q) => {
    setApiLoading(true);
    try {
      const res = await productService.getProducts({
        receta_ingredients_only: true,
        active_only: true,
        search: q || undefined,
      });
      const list = res.success && Array.isArray(res.data) ? res.data : [];
      setApiResults(
        list.map((p) => ({
          id: p.id,
          name: p.name,
          code: p.sku || p.code || '',
          unit_of_measure: p.unit_of_measure,
        }))
      );
    } catch {
      setApiResults([]);
    } finally {
      setApiLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode !== 'insumo') return;
    if (useInsumoCatalog) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadInsumos(text.trim());
    }, 280);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [text, mode, loadInsumos, useInsumoCatalog]);

  const filtered = useInsumoCatalog ? (filteredInsumoLocal || []) : mode === 'insumo' ? apiResults : filteredMock;

  useEffect(() => {
    setHighlight(0);
  }, [text, mode]);

  const updatePanelPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPanelStyle({
      position: 'fixed',
      top: Math.round(r.bottom + 4),
      left: Math.round(r.left),
      width: Math.round(r.width),
      zIndex: 9999,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return;
    }
    updatePanelPosition();
    const onWin = () => updatePanelPosition();
    window.addEventListener('scroll', onWin, true);
    window.addEventListener('resize', onWin);
    return () => {
      window.removeEventListener('scroll', onWin, true);
      window.removeEventListener('resize', onWin);
    };
  }, [open, updatePanelPosition, text]);

  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectProduct = (p) => {
    setText(p.name);
    if (mode === 'insumo' && onSelectProduct) {
      onSelectProduct({
        productId: p.id,
        label: p.name,
        sku: p.code || '',
        unit_of_measure: p.unit_of_measure,
      });
    } else if (onChange) {
      onChange(p.name);
    }
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && filtered.length > 0) {
      e.preventDefault();
      selectProduct(filtered[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!listRef.current || !open) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open, filtered]);

  const listEl =
    open && panelStyle ? (
      <ul
        id={listboxIdRef.current}
        ref={listRef}
        style={panelStyle}
        className="max-h-48 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        role="listbox"
      >
        {mode === 'insumo' && !useInsumoCatalog && apiLoading ? (
          <li className="px-3 py-2 text-sm text-gray-500">Buscando…</li>
        ) : filtered.length === 0 ? (
          <li className="px-3 py-2 text-sm text-gray-500">
            {mode === 'insumo' ? 'Sin insumos coincidentes' : 'Sin coincidencias'}
          </li>
        ) : (
          filtered.map((p, idx) => (
            <li
              key={p.id ?? `${p.name}-${idx}`}
              data-idx={idx}
              role="option"
              aria-selected={idx === highlight}
              className={`cursor-pointer px-3 py-2 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 ${
                idx === highlight ? 'bg-teal-50 text-teal-900' : 'hover:bg-gray-50 text-gray-800'
              }`}
              onMouseDown={(ev) => {
                ev.preventDefault();
                selectProduct(p);
              }}
              onMouseEnter={() => setHighlight(idx)}
            >
              <span className="font-medium">{p.name}</span>
              <span className="text-xs text-gray-500 font-mono">{p.code}</span>
            </li>
          ))
        )}
      </ul>
    ) : null;

  const handleInputChange = (e) => {
    const v = e.target.value;
    setText(v);
    if (mode === 'insumo' && onSelectProduct) {
      if (!v.trim()) {
        onSelectProduct({ productId: null, label: '', sku: '', unit_of_measure: null });
      } else if (productId != null) {
        onSelectProduct({ productId: null, label: v, sku: '', unit_of_measure: null });
      }
    } else if (onChange) {
      onChange(v);
    }
    setOpen(true);
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      <input
        type="text"
        value={text}
        onChange={handleInputChange}
        onFocus={() => {
          setOpen(true);
          if (mode === 'insumo' && !useInsumoCatalog) {
            loadInsumos(text.trim());
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listboxIdRef.current : undefined}
        role="combobox"
      />
      {typeof document !== 'undefined' && listEl ? createPortal(listEl, document.body) : null}
      {mode === 'mock' ? (
        <span className="sr-only">
          Catálogo local de demostración ({getMockProductosCatalogo().length} productos)
        </span>
      ) : (
        <span className="sr-only">Búsqueda de productos insumo por API</span>
      )}
    </div>
  );
};

export default ProductoBuscador;
